import type { CSSProperties, ReactNode } from 'react';
import { StatusDot, type StatusTone } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { DesktopMachine, DesktopModelTotal } from './desktop-types';
import { anyModelName, anyModelTone, failureIsRecent, sharesOf } from './desktop-words';
import { cardsLine, groupName, machineTotals, type MachineGroup } from './desktop-machines';
import { availabilityWords, changedWords } from './policy-words';
import { RenameMachine } from './RenameMachine';
import { cardName, gib } from './gpu-words';

/** Saves a machine's name; an empty name clears it. */
export type Rename = (worker: string, name: string) => Promise<void>;

const TONE: Record<DesktopMachine['state'], StatusTone> = {
  online: 'success',
  paused: 'warning',
  offline: 'danger',
};

/** The headline word for a machine. */
function stateWord(g: MachineGroup): string {
  if (g.state === 'offline') return 'Offline';
  if (g.state === 'paused') return g.policy?.policy?.paused ? 'Paused' : 'Paused at the desk';
  return g.cards.some((c) => c.working.length > 0) ? 'Working' : 'Online, idle';
}

/** A span's work as one bar, a segment per model, and the numbers under it. */
function WorkBar({ label, totals }: { label: string; totals: DesktopModelTotal[] }): ReactNode {
  const f = useFormat();
  const shares = sharesOf(totals);
  return (
    <div className="dg-work">
      <span className="dg-work__label">{label}</span>
      <span className="gpu-split__bar" aria-hidden="true">
        {shares.map((s) => (
          <span
            key={s.model}
            className={`gpu-mem__seg gpu-tone--${anyModelTone(s.model)}`}
            style={{ '--gpu-share': `${(s.share * 100).toFixed(1)}%` } as CSSProperties}
          />
        ))}
      </span>
      <span className="dg-work__nums">
        {shares.length === 0
          ? 'nothing'
          : shares.map((s, i) => (
              <span key={s.model} className="dg-work__num">
                {`${anyModelName(s.model)} ${f.int(s.calls)}${i < shares.length - 1 ? ' ·' : ''}`}
              </span>
            ))}
      </span>
    </div>
  );
}

/** One card of the machine: its name and memory, what it does now, and the models on it, each lit while it works. */
function CardRow({ m, zone }: { m: DesktopMachine; zone: string | undefined }): ReactNode {
  const f = useFormat();
  const word = m.state === 'offline' ? 'Offline' : availabilityWords(m.availability, zone);
  const off = m.state === 'offline' || (m.availability !== null && !m.availability.on);
  return (
    <li className={`dg-card${off ? ' dg-card--off' : ''}`} data-worker={m.worker}>
      <span className="dg-card__name">
        {cardName(m.card)}
        {m.totalMib > 0 ? <span className="dg-quiet"> · {gib(m.totalMib)} GiB</span> : null}
      </span>
      <span className={`dg-card__now${off ? ' dg-card__now--off' : ''}`}>{word}</span>
      <span className="dg-card__models">
        {m.models.length === 0 ? (
          <span className="dg-quiet">Nothing placed</span>
        ) : (
          m.models.map((model) => {
            const now = m.working.find((w) => w.model === model)?.inFlight ?? 0;
            return (
              <span
                key={model}
                className={`dg-model gpu-tone--${anyModelTone(model)}${now > 0 ? ' dg-model--busy' : ''}`}
              >
                {anyModelName(model)}
                {now > 0 ? <span className="dg-model__now">working now · {f.int(now)}</span> : null}
              </span>
            );
          })
        )}
      </span>
    </li>
  );
}

interface TileActions {
  onRename?: Rename | undefined;
  onEdit?: (() => void) | undefined;
}

/** Cards and schedule, and Rename, when the screen may save them. */
function Acts({ g, onRename, onEdit }: TileActions & { g: MachineGroup }): ReactNode {
  return (
    <span className="dg-tile__acts">
      {onEdit ? (
        <button type="button" className="dg-rename" onClick={onEdit}>
          Cards and schedule
        </button>
      ) : null}
      {onRename ? (
        <RenameMachine
          name={g.cards[0]?.displayName ?? ''}
          onSave={(name) => onRename(g.id, name)}
        />
      ) : null}
    </span>
  );
}

/** The machine's state in big type; an offline one since when. */
function State({ g }: { g: MachineGroup }): ReactNode {
  const f = useFormat();
  const word = stateWord(g);
  const lastSeen = g.cards
    .map((c) => c.lastSeen)
    .sort()
    .at(-1);
  return (
    <span className={`dg-state dg-state--${g.state}`}>
      <StatusDot tone={TONE[g.state]} label={word} />
      {word}
      {g.state === 'offline' && lastSeen ? (
        <span className="dg-offline">since {f.dateTime(lastSeen)}</span>
      ) : null}
    </span>
  );
}

/** The tile's top: the name people know it by, its cards and own name, its state, Rename and the editor. */
function Head({ g, ...acts }: TileActions & { g: MachineGroup }): ReactNode {
  const own = groupName(g) === g.id ? '' : ` · ${g.id}`;
  return (
    <header className="dg-tile__head">
      <span className="dg-tile__who">
        <strong className="dg-tile__name">{groupName(g)}</strong>
        <span className="dg-tile__card">
          {cardsLine(g)}
          {own}
        </span>
      </span>
      <State g={g} />
      <Acts g={g} {...acts} />
    </header>
  );
}

/**
 * Who changed the cards and schedule last, and a change still waiting for the machine; or, when something is wrong
 * (the relay refuses the change, or the machine's own copy breaks the rules), that instead of "waiting".
 */
function PolicyLine({ g }: { g: MachineGroup }): ReactNode {
  const p = g.policy;
  if (p?.problem) return <p className="dg-tile__policy dg-error">{p.problem}</p>;
  if (!p?.policy) return null;
  return (
    <p className="dg-tile__policy">
      {changedWords(p.policy)}
      {p.pending ? (
        <span className="dg-pending">
          {p.connected
            ? ' · Sent, not shown by the machine yet'
            : ' · Waiting for the machine: applies when it connects'}
        </span>
      ) : null}
    </p>
  );
}

/** The builds the machine's cards run, and its newest failure of the last day. */
function Foot({ g }: { g: MachineGroup }): ReactNode {
  const f = useFormat();
  const versions = [...new Set(g.cards.map((c) => c.version))].join(', ');
  const outdated = g.cards.some((c) => c.outdated);
  const failed = g.cards
    .filter((c) => failureIsRecent(c))
    .sort((a, b) => ((a.lastErrorAt ?? '') < (b.lastErrorAt ?? '') ? 1 : -1))[0];
  return (
    <p className="dg-tile__foot">
      <span className={outdated ? 'dg-version dg-version--old' : 'dg-version'}>
        {outdated ? `Worker v${versions}, update pending` : `Worker v${versions}`}
      </span>
      {failed?.lastErrorAt ? (
        <span className="dg-error">
          Last failure {f.ago(failed.lastErrorAt)}: {failed.lastError}
        </span>
      ) : null}
    </p>
  );
}

/** One machine: a row per card, its work over today, 7 and 30 days, its build, policy and last failure. */
export function MachineTile({ g, ...acts }: TileActions & { g: MachineGroup }): ReactNode {
  return (
    <article
      className={`dg-tile dg-tile--${g.state}`}
      data-machine={g.id}
      aria-label={groupName(g)}
    >
      <Head g={g} {...acts} />
      <ul className="dg-cards">
        {g.cards.map((m) => (
          <CardRow key={m.worker} m={m} zone={g.policy?.policy?.timeZone} />
        ))}
      </ul>
      <div className="dg-work--today">
        <WorkBar label="Today" totals={machineTotals(g, 'today')} />
      </div>
      <WorkBar label="7 days" totals={machineTotals(g, 'week')} />
      <WorkBar label="30 days" totals={machineTotals(g, 'month')} />
      <PolicyLine g={g} />
      <Foot g={g} />
    </article>
  );
}
